import type { ReactNode } from 'react';

/**
 * Shared public-site primitives, transcribed from the measured baseline.
 * See docs/architecture/CURRENT-PUBLIC-VISUAL-BASELINE.md §1 and §3.
 */

/**
 * Full-bleed hero. Baseline P-9: navy background, optional photo with a navy
 * overlay, white Montserrat H1.
 *
 * `size`: "lg" = 60px H1 (home, about, catalogue, for-schools)
 *         "sm" = 48px H1 (contact, quote)  — both measured from production.
 */
export function Hero({
  title,
  subtitle,
  image,
  size = 'lg',
  padding = 'py-24',
  children,
}: {
  title: string;
  subtitle?: string;
  image?: string;
  size?: 'lg' | 'sm';
  padding?: string;
  children?: ReactNode;
}) {
  return (
    <section className={`relative section-navy ${image ? '' : padding} overflow-hidden`}>
      {image && (
        <>
          {/* R-11: explicit dimensions + eager/high priority for the LCP image. */}
          <img
            src={image}
            alt=""
            width={1440}
            height={593}
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-navy/75" aria-hidden="true" />
        </>
      )}
      <div
        className={`container-content relative ${image ? 'flex min-h-[593px] flex-col justify-center py-20' : ''}`}
      >
        <h1
          className={`max-w-4xl font-heading font-bold text-white ${
            size === 'lg' ? 'text-4xl sm:text-5xl lg:text-[60px] lg:leading-[1.1]' : 'text-3xl sm:text-4xl lg:text-[48px] lg:leading-[1.15]'
          }`}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 max-w-2xl text-lg text-white/80">{subtitle}</p>
        )}
        {children && <div className="mt-8 flex flex-wrap gap-4">{children}</div>}
      </div>
    </section>
  );
}

/**
 * Standard content section. Baseline §1.3: 80px block padding, alternating
 * white / #f3f5f6 backgrounds. The alternation per page is load-bearing (P-3).
 */
export function Section({
  variant = 'white',
  title,
  intro,
  children,
  titleClassName,
}: {
  variant?: 'white' | 'muted' | 'navy';
  title?: string;
  intro?: string;
  children: ReactNode;
  titleClassName?: string;
}) {
  const bg =
    variant === 'muted' ? 'section-muted' : variant === 'navy' ? 'section-navy' : 'bg-white';
  const onNavy = variant === 'navy';
  return (
    <section className={`section ${bg}`}>
      <div className="container-content">
        {title && (
          <h2
            className={`font-heading text-3xl font-bold lg:text-[36px] ${
              onNavy ? 'text-white' : 'text-navy'
            } ${titleClassName ?? ''}`}
          >
            {title}
          </h2>
        )}
        {intro && (
          <p className={`mt-4 max-w-3xl text-lg ${onNavy ? 'text-white/80' : 'text-charcoal/80'}`}>
            {intro}
          </p>
        )}
        <div className={title || intro ? 'mt-12' : ''}>{children}</div>
      </div>
    </section>
  );
}

/**
 * Closing call-to-action band. Baseline: navy, 96px 16px padding, btn-band CTA.
 */
export function CtaBand({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action: ReactNode;
}) {
  return (
    <section className="section-navy px-4 py-24">
      <div className="container-content text-center">
        <h2 className="font-heading text-3xl font-bold text-white lg:text-[36px]">{title}</h2>
        {body && <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">{body}</p>}
        <div className="mt-8 flex justify-center">{action}</div>
      </div>
    </section>
  );
}

/**
 * Explicit empty state. Rule 3 — when the database has no rows we say so.
 * We never fabricate records to make a page look populated.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card px-6 py-16 text-center">
      <h3 className="font-heading text-xl font-bold text-navy">{title}</h3>
      {body && <p className="mx-auto mt-3 max-w-md text-charcoal/80">{body}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ title, body, onRetry }: { title: string; body?: string; onRetry?: () => void }) {
  return (
    <div className="card border-l-4 border-l-[#c0392b] px-6 py-10 text-center" role="alert">
      <h3 className="font-heading text-xl font-bold text-navy">{title}</h3>
      {body && <p className="mx-auto mt-3 max-w-md text-charcoal/80">{body}</p>}
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn btn-navy btn-small mt-6">
          Try again
        </button>
      )}
    </div>
  );
}

/** Skeleton placeholder used while a real request is in flight. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-muted ${className}`} aria-hidden="true" />;
}
